import 'intersection-observer';
import * as React from "react";
import { InView } from 'react-intersection-observer';
import { IOWidget, Updater, fromJSX } from "widgets-for-react";
import { blockInviewHandler } from "../../../drupal/rendering/inview_utils.mod";
import { ImageSize } from "../types.gen";
import { getClassName, link } from "../utils.gen";
import { ImageContainer } from "./ImageContainer.gen";
import { CMSState } from '../../../publicSite/drupalPage/state'

export type CtaBlockProps = {
  index: number
  title?: React.ReactNode
  intro: string
  display_option: string
  image?: number
  image_alt?: string
  image_size?: ImageSize
  get_default_data_src:(() => ImageSize)
  get_default_data_srcset:(() => [ImageSize, string][])
  buttons: "none" | "primary" | "primary_secondary"
  link_type: boolean
  primary_button?: ({
    kind: "link"
    is_link_external: boolean
    target: "_blank" | "_self"
    url: string
  }) & { title: string }
  secondary_button?: ({
    kind: "link"
    is_link_external: boolean
    target: "_blank" | "_self"
    url: string
  }) & { title: string }
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type CtaBlockStyle = Partial<{
  block_contentwidth_classname: string
  block_fullwidth_classname: string
  inview_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  content_classname: string
  text_classname: string
  footer_classname: string
  visual_classname: string
  image_classname: string
  clickable_wrapper_classname: string
  clickable_primary_classname: string
  clickable_secondary_classname: string
}>

export function CtaBlock(
  props: CtaBlockProps,
  options?: { style?: CtaBlockStyle; key?: string }
): IOWidget<CMSState, Updater<CMSState>> {
  return (cmsState: CMSState) =>
    fromJSX(
      _ => (
        <InView
          as="section"
          onChange={(inView) => blockInviewHandler('cta', inView, `io-${options && options.key ? options.key : props.index}`, getClassName(options, "inview_classname", "inview"))}
          triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
          threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
          id={`io-${options && options.key ? options.key : props.index}`} 
          className={getClassName(options, props.display_option === 'content-width' ? "block_contentwidth_classname" : "block_fullwidth_classname", props.display_option === 'content-width' ? "container py-5" : "container-fluid bg-light my-5")}
          key={options && options.key ? options.key : `block-${props.index}`}
        >
          <div className={getClassName(options, "inner_classname", `container d-flex ${props.display_option === 'content-width' ? "bg-light" : ""}`)}>
            <div className={getClassName(options, "content_classname", "p-5")}>
              {props.title && (
                <header className={getClassName(options, "header_classname", "")}>
                  <h2 className={getClassName(options, "title_classname", "text-center mb-3")}>{props.title}</h2>
                </header>
              )}
  
              <p className={getClassName(options, "text_classname", "lead text-center")}>{props.intro}</p>
    
              {(props.primary_button || props.secondary_button) && (
                <footer className={getClassName(options, "footer_classname", "text-center")}>
                  {props.primary_button && props.primary_button.title && props.primary_button.url
                    ? <div className={getClassName(options, "clickable_wrapper_classname", "")}>
                        {link(props.primary_button,
                          {
                            classname: 
                              props.link_type === false 
                                ? getClassName(options, "clickable_primary_classname", "btn btn-dark mr-2" ) 
                                : getClassName(options, "clickable_secondary_classname", "btn btn-link px-0" )
                          },
                          props.primary_button.title,
                          () => {
                            window.dataLayer.push({
                              event: 'event.refresco',
                              category: 'CTABlock',
                              action: props.primary_button.title,
                              label: window.location.pathname,
                            })
                          }
                        )}
                      </div>
                    : null}
                  {props.secondary_button && props.secondary_button.title && props.secondary_button.url
                    ? <div className={getClassName(options, "clickable_wrapper_classname", "")}>
                        {link(props.secondary_button,
                          {classname: getClassName(options, "clickable_secondary_classname", "btn btn-link px-0" )},
                          props.secondary_button.title,
                          () => {
                            window.dataLayer.push({
                              event: 'event.refresco',
                              category: 'CTABlock',
                              action: props.primary_button.title,
                              label: window.location.pathname,
                            })
                          } 
                        )}
                      </div>
                    : null}
                </footer>
              )}
            </div>

            {props.image &&
              <div className={getClassName(options, "visual_classname", "w-100")}>
                {cmsState.images.has(props.image) &&
                  ImageContainer(
                    cmsState.images.get(props.image),
                    props.image_size,
                    getClassName(options, "image_classname", `w-100 ${props.display_option !== 'content-width' ? "d-block ml-auto" : ""}`),
                    props.image_alt,
                    props.get_default_data_src,
                    props.get_default_data_srcset
                  ).run(_ => { })
                }
              </div>
            }
          </div>
        </InView>
      ),
    )
}
